

import api from "@/services/api"
import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { BModal } from "bootstrap-vue"
import { Update } from "@/types/update"
import { Getter, Mutation, Action, State } from "vuex-class"
import VuePerfectScrollBar from "vue-perfect-scrollbar"
import { Team } from "@/types"
import DataTable from "@/components/Config/DataTable.vue"
import HelpIcon from "@/components/New/Page/PageComponents/HelpIcon.vue"
import UpdatesEditor from "@/components/Updates/UpdatesEditor.vue"
import UpdatesViewer from "./UpdatesViewer.vue"
import UserImage from "@/components/UserImage.vue"
import { UserWithFields } from "@/types/user"
import SkeletonLoader from "../SkeletonLoader.vue"
import VueSelect from "vue-select"
import { formatDateMixin } from "@/mixins/formatDateMixin"

@Component({
	components: {
		"scrollbar": VuePerfectScrollBar,
		"data-table": DataTable,
		"help-icon": HelpIcon,
		"updates-viewer": UpdatesViewer,
		"updates-editor": UpdatesEditor,
		"user-image": UserImage,
		"loader": SkeletonLoader,
		"v-select": VueSelect
	}
})
export default class UpdatesSetup extends Vue {
	// ---- Props ----

	@Prop({ type: Number, default: 50 })
	public updatesPerPage!: number

	// ---- Vuex ----

	@Getter("getTeamsById")
	public teamsById!: { [key: string]: Team };

	// ---- Data ----

	// the Updates last fetched
	private updates: Update[] = []

	// the number of Updates being displayed after filter is applied
	private filteredUpdatesLength: number | null = null

	// the update to pass to modals
	private focusedUpdate: Update | null = null

	// toggles for child modals
	public showUpdatesViewer: boolean = false
	public showUpdatesEditor: boolean = false
	private deletingUpdate: boolean = false

	// Loaded user details stored against user Id
	private loadedUsers: { [key: string]: Promise<UserWithFields> } = {}

	public filter: string = "";

	public currentPage: number | null = null

	// whether updates are currently being fetched
	public isFetching: boolean = false

	perPageOptions = [ 10, 50, 100, 250, 500, "All"	]
	public perPage: number = this.updatesPerPage

	// ---- Lifecycle Methods ----

	public mounted () {
		this.fetchUpdates()
	}

	@Watch("updatesPerPage", { immediate: true })
	public updatesPerPageChanged (newValue: number) {
		this.perPage = newValue
	}

	// ---- Computed Properties ----

	private get columns () : any[] {
		return [
			{
				key: "title",
				label: "Title",
				sortable: true,
				thStyle: "width: 15%;"
			},
			{
				key: "body",
				label: "Body",
				sortable: true,
				formatter: (value: string) => this.stripHtml(value),
				filterByFormatted: true,
				sortByFormatted: true
			},
			{
				key: "teams",
				label: "Visible To",
				sortable: true,
				formatter: (value: string[]) => this.teamsDisplayString(value),
				filterByFormatted: true,
				sortByFormatted: true
			},
			{
				key: "lastUpdated",
				label: "Last Modified",
				sortable: true,
				dataType: "date-time",
				formatter: (value: string) => formatDateMixin.methods.displayDateFromISOString(value),
				filterByFormatted: true
			},
			{
				key: "created",
				label: "Created",
				sortable: true,
				dataType: "date-time",
				formatter: (value: string) => formatDateMixin.methods.displayDateFromISOString(value),
				filterByFormatted: true
			},
			{
				key: "createdBy",
				label: "Created By",
				sortable: true
			},
			{
				key: "editDelete",
				label: ""
			}
		]
	}

	public get noUpdates (): boolean {
		return !this.updates || this.updates.length === 0
	}

	public get getCurrentPage (): number {
		return (this.updatesPerPage && this.updates && this.updates.length > this.updatesPerPage &&
			this.currentPage)
			? this.currentPage : 1
	}

	public get focusedUpdateNameDisplay (): string {
		if (!this.focusedUpdate) {
			return ""
		}

		// maxTitleDisplayLength not to be set below 20 without adjusting shortenedEndingLength
		const maxTitleDisplayLength = 50
		const shortenedEndingLength = 10

		if (this.focusedUpdate.title.length <= maxTitleDisplayLength) {
			return this.focusedUpdate.title
		}

		const titleEnding = this.focusedUpdate.title.slice((this.focusedUpdate.title.length - shortenedEndingLength - 1),
			this.focusedUpdate.title.length).trim()

		return this.focusedUpdate.title.slice(0, maxTitleDisplayLength - 2).trim() + "- ... " + titleEnding
	}

	public get displayedUpdatesLength (): number {
		return this.filteredUpdatesLength !== null && this.filteredUpdatesLength > 0
			? this.filteredUpdatesLength : this.updates.length
	}

	// ---- Methods/Utils ----

	public async fetchUpdates (): Promise<void> {
		this.isFetching = true

		this.updates = await api.updatesGetAll()
		this.currentPage = 1

		this.isFetching = false
	}

	public editUpdate (update: Update) {
		this.focusedUpdate = update
		this.showUpdatesEditor = true
	}

	public async deleteUpdate (update: Update | null) {
		await api.updatesDelete(update!.id)
		this.deletingUpdate = false
		this.focusedUpdate = null
		this.fetchUpdates()
	}

	public confirmDeleteUpdate (update: Update) {
		this.focusedUpdate = update
		this.deletingUpdate = true
	}

	public createNewUpdate () {
		this.focusedUpdate = null
		this.showUpdatesEditor = true
	}

	public createModalVisibilityChanged (newValue: boolean, modified: boolean) {
		if (newValue === false) {
			this.showUpdatesEditor = newValue
			if (modified) {
				this.fetchUpdates()
			}
		}
	}

	public viewModalVisibilityChanged (newValue: boolean) {
		if (newValue === false) {
			this.showUpdatesViewer = newValue
		}
	}

	public teamsDisplayString (teams: string[]): string {
		if (!teams || teams.length === 0) {
			return "Everyone"
		}

		let output: string = ""
		teams.forEach(team => output += this.teamsById[team].title + ", ")

		return output.slice(0, output.length - 2)
	}

	public async getUserById (userId: string): Promise<UserWithFields> {
		if (!this.loadedUsers[userId]) {
			this.loadedUsers[userId] = api.userFieldDetails(userId)
		}
		return this.loadedUsers[userId]
	}

	private stripHtml (content: string) {
		return content.replace(/<[^>]*>?/gm, "")
	}

	private updateRowClicked (item: Update) {
		if (!item) {
			return
		}
		this.focusedUpdate = item
		this.showUpdatesViewer = true
	}

	public cancelDeleteUpdate () {
		this.focusedUpdate = null
		this.deletingUpdate = false
	}

	public filterChanged (filteredUpdates: Update[]) {
		this.filteredUpdatesLength = filteredUpdates.length
		this.currentPage = 1
	}
}

